import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, HostListener, inject, Input, OnChanges, OnInit, Optional, Output, Self, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroupDirective, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, NgForm, ReactiveFormsModule, UntypedFormControl, ValidationErrors, Validator } from '@angular/forms';
import { TerraUtils } from '../../TerraUtils';
import { CommonModule, DatePipe } from '@angular/common';
import { ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'terra-custom-utc-date-picker',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './custom-utc-date-picker.component.html',
  styleUrl: './custom-utc-date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CustomUtcDatePickerComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => CustomUtcDatePickerComponent),
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }
  ],
})
export class CustomUtcDatePickerComponent implements OnInit, ControlValueAccessor, Validator {
private readonly datePipe = inject(DatePipe);

  @Input() id:string = '';
  @Input() label:string = '';
  @Input() fieldClass:string = '';
  @Input() placeholder:string = '';
  @Input() startAt:Date | string | null = null;
  @Input() startView:string = 'month';
  @Input() isSubmitted:boolean = false;
  @Input() touched:boolean = false;
  @Input() requiredError:boolean = false;
  @Input() minDate:Date | null = null;
  @Input() maxDate:Date | null = null;
  @Input() minError:boolean = false;
  @Input() maxError:boolean = false;
  @Input() minErrorTxt:string = `Please select a date not before ${typeof this.minDate === 'string' ? this.minDate : this.datePipe.transform(this.minDate)}`;
  @Input() maxErrorTxt:string = `Please select a date not after ${typeof this.maxDate === 'string' ? this.maxDate : this.datePipe.transform(this.maxDate)}`;

  @Output() inputChange = new EventEmitter<Date>();


  value!: Date;
  disabled: boolean = false;
  matcher = new CustomDateErrorStateMatcher(this);

  ngOnInit(): void {
  }

  addEvent(name:string, event: any){
    this.valueChanged(event.value as Date)
  }

  dateChanged(event: any): void {
    this.valueChanged(event.value as Date)
  }

  valueChanged(val: Date) {
    const newDate = TerraUtils.forceUtc(val);
    this.onChange(newDate);//, 0,0,0));
    this.inputChange.emit(newDate);
    this.markAsTouched();
    this.onValidatorChange(); 
  }

  private onChange = (value: Date) => { };
  private onTouched: () => void = () => {};

  private onValidatorChange = () => {};

  writeValue(value): void {
    this.value = TerraUtils.forceUtcToLocal(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched(); // Notify Angular forms that the control is touched
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const date = TerraUtils.forceUtcToLocal(control.value as Date)
    if (this.minDate && date && TerraUtils.compareDateWithNull(date, this.minDate) === 1) {
      return { minDate: { valid: false } };
    }
    if (this.maxDate && date && TerraUtils.compareDateWithNull(date, this.maxDate) === -1) {
      return { maxDate: { valid: false } };
    }
    return null;
  }
}

export class CustomDateErrorStateMatcher implements ErrorStateMatcher {
  private _component: CustomUtcDatePickerComponent
  constructor(component: CustomUtcDatePickerComponent){
    this._component = component;
  }
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const invalid = this._component.requiredError || this._component.minError || this._component.maxError;
    const alertError = this._component.touched || this._component.isSubmitted;
    return invalid && alertError;
  }
}
